import { pluralize } from "@common/helpers/string";
import { Button } from "@design-system/Button/Button";
import { Shortcut } from "@design-system/Shortcut/Shortcut";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { Bill, BillSource } from "@common/types/bill";
import { BillView } from "@common/types/views/billViews";
import { View } from "@common/types/views/views";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { CommandIslandTotal } from "@design-system/CommandIsland/CommandIslandTotal";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { BulkSelectionBar } from "@components/views/BulkSelectionBar";
import { OnChangeFn, RowSelectionState } from "@tanstack/react-table";
import React, { useCallback, useMemo } from "react";
import { Pagination } from "@design-system/Pagination/Pagination";
import { HStack } from "@design-system/HStack/HStack";
import { useViewTableLoadMore } from "@hooks/useLoadMoreTableState";
import { PaginatedBillsTableContext } from "../../PaginatedBillsTable.context";
import { useBillBulkCommandActions } from "../../useBillBulkCommandActions";
import { useBillTableEmptyView } from "../../useBillTableEmptyView";
import { useBillsBulkCreateTask } from "../../useBillsBulkCreateTask";
import { BillScheduleWiring } from "@components/bills/schedules/billScheduleColumns";
import { BillsView } from "./BillsView";

const isSelectableBill = () => true;

interface BillsViewContainerProps {
    view: BillView;
    onBillClick: (bill?: Bill) => void;
    selectedBillId?: string;
    rowSelection?: RowSelectionState;
    onRowSelectionChange?: OnChangeFn<RowSelectionState>;
    onClearSelection?: () => void;
    onEditBills?: (bills: Bill[]) => void;
    hasAnyAccrualLedgers?: boolean;
    hasConnectedBillAccounts: boolean;
    onClearFilters: () => void;
    /** Replaces the workspace-wide empty states, for tables scoped by a task. */
    emptyView?: () => React.ReactNode;
    onViewChange?: (view: View) => void;
    scheduleWiring?: BillScheduleWiring;
}

export const BillsViewContainer: React.FC<BillsViewContainerProps> = ({
    view,
    onBillClick,
    selectedBillId,
    rowSelection,
    onRowSelectionChange,
    onClearSelection,
    onEditBills,
    hasAnyAccrualLedgers = false,
    hasConnectedBillAccounts,
    onClearFilters,
    emptyView: customEmptyView,
    onViewChange,
    scheduleWiring,
}) => {
    const {
        bills,
        billCount,
        currentSort,
        handleSortChange,
        currentPage,
        totalPages,
        setPage,
        isLoading,
        loadMore,
    } = React.useContext(PaginatedBillsTableContext);
    const tableLoadMore = useViewTableLoadMore(
        loadMore,
        "Failed to load more bills",
    );
    const hasPagination = !loadMore && totalPages > 1;

    const { emptyView: defaultEmptyView, shouldHideFooter } =
        useBillTableEmptyView({
            isLoading,
            filteredCount: billCount,
            hasConnectedBillAccounts,
            onClearFilters,
            viewKind: "bills",
        });

    const { createTaskFromBills } = useBillsBulkCreateTask({ view });
    const selectedBills = useMemo(
        () => bills?.filter((bill) => rowSelection?.[bill.id]) ?? [],
        [bills, rowSelection],
    );
    const handleCreateTask = useCallback(
        () => createTaskFromBills(selectedBills),
        [createTaskFromBills, selectedBills],
    );
    const { handleOpenBulkActions } = useBillBulkCommandActions({
        selectedCount: selectedBills.length,
        onCreateTask: handleCreateTask,
    });
    // Only importer bills can be edited in bulk; the other bills are selectable
    // for actions that read them, such as creating a task.
    const isEditAvailable =
        !!onEditBills &&
        selectedBills.length > 0 &&
        selectedBills.every((bill) => bill.source === BillSource.IMPORTER);

    // Selection is by id, but the actionable set is the loaded page, so clear
    // it when the page changes to keep the footer count and the actions in sync.
    const handlePageChange = useCallback(
        (nextPage: number) => {
            onClearSelection?.();
            setPage(nextPage);
        },
        [onClearSelection, setPage],
    );

    const footer = (
        <CommandIsland
            sticky
            className={commandIslandStyles.bulkActionsStickyFooter}
        >
            <HStack gap={8}>
                {hasPagination && (
                    <Pagination
                        size="md"
                        pageCount={totalPages}
                        currentPage={currentPage}
                        onPageChange={handlePageChange}
                    />
                )}
                <CommandIslandTotal>
                    {billCount} {pluralize("bill", billCount)}
                </CommandIslandTotal>
                <BulkSelectionBar
                    items={bills}
                    rowSelection={rowSelection}
                    onClearSelection={onClearSelection}
                    onEditItems={isEditAvailable ? onEditBills : undefined}
                />
            </HStack>
            {selectedBills.length > 0 && (
                <Tooltip content="Open bulk actions">
                    <Button
                        variant="primary"
                        size="md"
                        onClick={handleOpenBulkActions}
                        rightIcon={
                            <span data-slot-box="">
                                <Shortcut keys="mod+k" />
                            </span>
                        }
                    >
                        Actions
                    </Button>
                </Tooltip>
            )}
        </CommandIsland>
    );

    return (
        <BillsView
            view={view}
            bills={bills ?? null}
            selectedBillId={selectedBillId}
            onBillClick={onBillClick}
            sort={currentSort}
            onSortChange={handleSortChange}
            footer={shouldHideFooter && !customEmptyView ? undefined : footer}
            rowSelection={rowSelection}
            onRowSelectionChange={onRowSelectionChange}
            hasAnyAccrualLedgers={hasAnyAccrualLedgers}
            canSelectBill={isSelectableBill}
            emptyView={customEmptyView ?? defaultEmptyView}
            onViewChange={onViewChange}
            scheduleWiring={scheduleWiring}
            loadMore={tableLoadMore}
        />
    );
};
